import { useCallback, useMemo } from "react";

import { useLiveRegion } from "./liveRegion";

/**
 * ARIA attributes and utilities for carousel components
 */
export interface CarouselAriaProps {
  "aria-describedby"?: string;
  "aria-label"?: string;
  "aria-roledescription": string;
}

/**
 * Generate ARIA attributes for carousel container
 */
export function useCarouselAria(
  options: {
    carouselId?: string;
    currentSlide?: number;
    describedBy?: string;
    label?: string;
    totalSlides?: number;
  } = {}
): CarouselAriaProps & { carouselId: string } {
  const {
    carouselId: providedCarouselId,
    describedBy,
    label,
    totalSlides,
  } = options;

  const ariaLabel =
    label || (totalSlides ? `Carousel with ${totalSlides} slides` : "Carousel");

  // Generate a consistent carousel ID for testing
  const carouselId = useMemo(
    () =>
      providedCarouselId ||
      `carousel-${Math.random().toString(36).substring(2, 11)}`,
    [providedCarouselId]
  );

  return {
    "aria-roledescription": "carousel",
    "aria-label": ariaLabel,
    carouselId,
    ...(describedBy && { "aria-describedby": describedBy }),
  };
}

/**
 * Generate ARIA attributes for carousel slide
 */
export function useCarouselSlideAria(options: {
  isActive?: boolean;
  label?: string;
  slideIndex: number;
  totalSlides: number;
}): {
  "aria-hidden": boolean;
  "aria-label": string;
  "aria-roledescription": string;
  tabIndex: number;
} {
  const { isActive = false, label, slideIndex, totalSlides } = options;

  const slideLabel = label || `Slide ${slideIndex + 1} of ${totalSlides}`;

  return {
    "aria-roledescription": "slide",
    "aria-label": slideLabel,
    "aria-hidden": !isActive,
    tabIndex: isActive ? 0 : -1,
  };
}

/**
 * Generate ARIA attributes for carousel navigation buttons
 */
export function useCarouselNavigationAria(options: {
  carouselId?: string;
  currentSlide: number;
  direction: "previous" | "next";
  disabled?: boolean;
  totalSlides: number;
}): {
  "aria-controls": string;
  "aria-disabled": boolean;
  "aria-label": string;
} {
  const {
    carouselId,
    currentSlide,
    direction,
    disabled = false,
    totalSlides,
  } = options;

  const isAtStart = currentSlide === 0;
  const isAtEnd = currentSlide === totalSlides - 1;

  const isDisabled =
    disabled ||
    (direction === "previous" && isAtStart) ||
    (direction === "next" && isAtEnd);

  const label = isDisabled
    ? `${direction === "previous" ? "Previous" : "Next"} slide (disabled)`
    : `Go to ${direction === "previous" ? "previous" : "next"} slide, ${
        direction === "previous" ? currentSlide : currentSlide + 2
      } of ${totalSlides}`;

  const trackId = useMemo(
    () =>
      carouselId
        ? `${carouselId}-track`
        : `carousel-track-${Math.random().toString(36).substring(2, 11)}`,
    [carouselId]
  );

  return {
    "aria-label": label,
    "aria-controls": trackId,
    "aria-disabled": isDisabled,
  };
}

/**
 * Hook for managing carousel announcements
 */
export function useCarouselAnnouncements() {
  const { announce } = useLiveRegion();

  const announceSlideChange = useCallback(
    (currentSlide: number, totalSlides: number) => {
      announce(`Slide ${currentSlide + 1} of ${totalSlides}`);
    },
    [announce]
  );

  return {
    announceSlideChange,
  };
}

/**
 * ARIA attributes for carousel pagination dots
 */
export function useCarouselPaginationAria(options: {
  carouselId?: string;
  currentSlide: number;
  slideIndex: number;
  totalSlides: number;
}): {
  "aria-controls": string;
  "aria-current": "true" | "false";
  "aria-label": string;
} {
  const { carouselId, currentSlide, slideIndex, totalSlides } = options;

  const trackId = useMemo(
    () =>
      carouselId
        ? `${carouselId}-track`
        : `carousel-track-${Math.random().toString(36).substring(2, 11)}`,
    [carouselId]
  );

  return {
    "aria-label": `Go to slide ${slideIndex + 1} of ${totalSlides}`,
    "aria-current": slideIndex === currentSlide ? "true" : "false",
    "aria-controls": trackId,
  };
}
